import { Delete, TipsAndUpdates } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParamsContext } from "../../../../../context/juno/ParamsContext";
import { LoRA, StyleFluxJuno } from "../../../../../types/junoTypes";
import { styleTranslate } from "../../../../../utils/styleTranslate";
import RecommendIcon from "@mui/icons-material/Recommend";

const Lora = () => {
  const { t } = useTranslation();
  const { loras, setLoras, aiEngine } = useParamsContext();
  const [localLoras, setLocalLoras] = useState<LoRA[]>([]);

  // FLUX系モデルかどうかを判定
  const fluxModels = [
    "FluxJuno",
    "Flux1DevCannyLoRA",
    "Flux1DevDepthLoRA",
    "Flux1DevFill",
    "Flux1ProJuno",
    "Flux11ProUltraJuno",
    "Flux1ProCannyJuno",
    "Flux1ProDepthJuno",
    "Flux1ProFillJuno",
    "FluxJuggernautJuno",
  ];

  const isFluxModel = fluxModels.includes(aiEngine!);

  // aiEngineが変更されたときにローカル状態を更新
  useEffect(() => {
    if (aiEngine && isFluxModel) {
      const currentLoras = loras[aiEngine] || [];
      setLocalLoras(currentLoras);
    }
  }, [aiEngine, loras, isFluxModel]);

  if (!isFluxModel) {
    return null;
  }
  const proJuno = ["Flux1ProJuno", "Flux11ProUltraJuno", "Flux1ProCannyJuno", "Flux1ProDepthJuno", "Flux1ProFillJuno"];
  const isProJuno = proJuno.includes(aiEngine!);

  // 使用可能なLoRAオプション
  const getAvailableLoraOptions = (): StyleFluxJuno[] => {
    const baseOptions: StyleFluxJuno[] = [];

    if (isProJuno) {
      baseOptions.push("ANIME_PRO");
      if (aiEngine !== "Flux1ProJuno") {
        baseOptions.push("JAPANESE_PRO");
      }
      baseOptions.push("JAPANESE_PRO_V1_1", "CINEMATIC_PRO", "MIDJOURNEY_PRO");
    } else {
      if (["Flux1DevCannyLoRA", "Flux1DevDepthLoRA", "Flux1DevFill"].includes(aiEngine!)) {
        baseOptions.push("GENERAL");
      }
      baseOptions.push("ANIME", "JAPANESE", "JAPANESE11", "JAPANESE11B", "CINEMATIC", "MIDJOURNEY");
    }

    // 既に選択されているLoRAを除外
    const selectedNames = localLoras.map((lora) => lora.name);
    return baseOptions.filter((option) => !selectedNames.includes(option));
  };

  const availableOptions = getAvailableLoraOptions();

  // LoRAごとの初期Weight設定（オススメ値）
  const getInitialWeight = (name: StyleFluxJuno): number => {
    const weights: Record<StyleFluxJuno, number> = {
      GENERAL: 1.0,
      ANIME: 0.7,
      ANIME_PRO: 0.8,
      JAPANESE: 0.8,
      JAPANESE11: 0.9,
      JAPANESE11B: 1.0,
      JAPANESE_PRO: 0.75,
      JAPANESE_PRO_V1_1: 0.75,
      CINEMATIC: 0.7,
      CINEMATIC_PRO: 0.7,
      MIDJOURNEY: 0.75,
      MIDJOURNEY_PRO: 0.75,
    };
    return weights[name] || 0.8;
  };

  // LoRA追加
  const addLora = (name: StyleFluxJuno) => {
    const newLora: LoRA = { name, weight: getInitialWeight(name) };
    const updatedLoras = [...localLoras, newLora];

    setLocalLoras(updatedLoras);
    setLoras({ ...loras, [aiEngine!]: updatedLoras });
  };

  // LoRA削除
  const removeLora = (index: number) => {
    const updatedLoras = localLoras.filter((_, i) => i !== index);
    setLocalLoras(updatedLoras);
    setLoras({ ...loras, [aiEngine!]: updatedLoras });
  };

  // LoRA重み変更（ローカル状態のみ更新）
  const updateLoraWeight = (index: number, weight: number) => {
    const updatedLoras = [...localLoras];
    updatedLoras[index].weight = weight;
    setLocalLoras(updatedLoras);
  };

  // 重み変更確定時にコンテキストを更新
  const handleWeightChangeCommitted = (index: number, weight: number) => {
    const updatedLoras = [...localLoras];
    updatedLoras[index].weight = weight;
    setLoras({ ...loras, [aiEngine!]: updatedLoras });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 2 }} gap={1}>
        <Typography variant="body2">{t("juno.input.lora.title")}</Typography>
      </Box>

      {/* 選択されたLoRAの表示 */}
      {localLoras.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {localLoras.map((lora, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Chip
                  label={t(styleTranslate(lora.name))}
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                  color="secondary"
                />
                {/* 削除ボタン（すべてのモデルで表示） */}
                <IconButton size="small" onClick={() => removeLora(index)} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="caption" sx={{ minWidth: "40px" }}>
                  {t("juno.input.style.weight")}
                </Typography>
                <Slider
                  value={lora.weight}
                  onChange={(_, value) => updateLoraWeight(index, value as number)}
                  onChangeCommitted={(_, value) => handleWeightChangeCommitted(index, value as number)}
                  min={0.1}
                  max={1.5}
                  step={0.05}
                  marks={[
                    {
                      value: getInitialWeight(lora.name),
                      label: (
                        <Tooltip placement="bottom" title={`Recommended: ${getInitialWeight(lora.name)}`}>
                          <div>
                            <RecommendIcon
                              sx={{
                                fontSize: "0.8rem",
                                color: lora.weight === getInitialWeight(lora.name) ? "warning.light" : "text.secondary",
                              }}
                            />
                          </div>
                        </Tooltip>
                      ),
                    },
                  ]}
                  sx={{
                    flex: 1,
                    "& .MuiSlider-markLabel": {
                      fontSize: "0.6rem",
                    },
                  }}
                />
                <Box
                  sx={{
                    width: "50px",
                    bgcolor: "rgba(0, 0, 0, 0.1)",
                    px: 1,
                    py: 0.5,
                    borderRadius: "5px",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption">{lora.weight}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* LoRA追加ボタン */}
      {availableOptions.length > 0 && localLoras.length < (isProJuno ? 1 : 2) && (
        <Box>
          <FormControl fullWidth size="small">
            <InputLabel>{t("juno.input.lora.add")}</InputLabel>
            <Select
              value=""
              label={t("juno.input.lora.add")}
              onChange={(event) => {
                const value = event.target.value as StyleFluxJuno;
                if (value) {
                  addLora(value);
                }
              }}
            >
              {availableOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(styleTranslate(option))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* 制限メッセージ */}
      {localLoras.length >= (isProJuno ? 1 : 2) && (
        <Typography variant="caption" color="text.secondary">
          {t(isProJuno ? "juno.input.lora.proMaxLimit" : "juno.input.lora.maxLimit")}
        </Typography>
      )}
    </Box>
  );
};

export default Lora;
